import { Box } from "@mui/material";
import classNames from "classnames";
import { PluggableList } from "node_modules/react-markdown/lib";
import * as React from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import remarkEmoji from "remark-emoji";
import remarkGfm from "remark-gfm";

import {
  isDatasetLink,
  isFileLink,
  isMessagePathLink,
  isTimestampLink,
  isTopicLink,
  isRobotoUri,
} from "@/shared/components/RobotoMarkdown/magicLinks.ts";
import { browserSupportsNamedGroups } from "@/shared/text";

import {
  preProcessContent,
  convertToMarkdownWriteToClipboard,
} from "./helpers";
import styles from "./RobotoMarkdown.module.css";
import {
  RobotoMarkdownLink,
  DatasetLinkRenderer,
  FileLinkRenderer,
  MessagePathLinkRenderer,
  TopicLinkRenderer,
} from "./RobotoMarkdownLink";

type RobotoMarkdownProps = {
  content: string;
  /**
   * Additional CSS class name to apply to the markdown container.
   * Can be used to style individual HTML elements rendered from markdown,
   * similar to how RobotoMarkdown.module.css is used.
   */
  className?: string;
  variant?: "normal" | "compact";
  datasetLinkRenderer?: DatasetLinkRenderer;
  fileLinkRenderer?: FileLinkRenderer;
  topicLinkRenderer?: TopicLinkRenderer;
  messagePathLinkRenderer?: MessagePathLinkRenderer;
  onLinkClick?: () => void;
};

export const RobotoMarkdown: React.FC<RobotoMarkdownProps> = ({
  content,
  className,
  datasetLinkRenderer,
  fileLinkRenderer,
  topicLinkRenderer,
  messagePathLinkRenderer,
  onLinkClick,
  variant = "compact",
}: RobotoMarkdownProps) => {
  // Conditionally create remarkPlugins based on named group support
  const remarkPlugins: PluggableList = [
    remarkEmoji,
    ...(browserSupportsNamedGroups() ? [remarkGfm] : []),
  ];

  // Pre-process the mentions, place the @ symbol inside the link for correct formatting
  // and make the link navigate to '/users/{userId}'
  const formattedContent = preProcessContent(content);

  const containerClassName = React.useMemo(() => {
    const classNamesToCombine = [];
    if (variant === "compact") {
      classNamesToCombine.push(styles.markdownVariantCompact);
    } else if (variant === "normal") {
      classNamesToCombine.push(styles.markdownVariantNormal);
    }

    if (className) {
      classNamesToCombine.push(className);
    }

    if (classNamesToCombine.length === 0) {
      return undefined;
    }

    return classNames(...classNamesToCombine);
  }, [className, variant]);

  return (
    <Box
      data-cy="markdown-container"
      onCopy={(e) => {
        e.preventDefault();

        const selection = window.getSelection();
        if (!selection) {
          return;
        }

        // generate markdown from single element
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          let commonAncestor: Node | HTMLElement | null =
            range.commonAncestorContainer;

          if (commonAncestor.nodeType === Node.TEXT_NODE) {
            commonAncestor = commonAncestor.parentElement;
          }

          const selectedContent = range.cloneContents();

          if (
            commonAncestor &&
            commonAncestor?.textContent?.trim() ===
              selection.toString().trim() &&
            selectedContent.childNodes.length === 1
          ) {
            const anchorNode = selection.anchorNode;
            const parentElement = anchorNode?.parentElement;
            const htmlString = parentElement?.outerHTML ?? "";

            convertToMarkdownWriteToClipboard(htmlString);

            return;
          }
        }

        // generate markdown from multiple elements
        const container = document.createElement("div");
        for (let i = 0; i < selection.rangeCount; i++) {
          container.appendChild(selection.getRangeAt(i).cloneContents());
        }

        convertToMarkdownWriteToClipboard(container.innerHTML);
      }}
    >
      <ReactMarkdown
        rehypePlugins={[[rehypeExternalLinks, { target: "_blank" }]]}
        remarkPlugins={remarkPlugins}
        className={containerClassName}
        urlTransform={(url) => {
          if (
            isTimestampLink(url) ||
            isDatasetLink(url) ||
            isFileLink(url) ||
            isTopicLink(url) ||
            isMessagePathLink(url) ||
            isRobotoUri(url)
          ) {
            return url;
          }

          return defaultUrlTransform(url);
        }}
        components={{
          a: ({ href, children }) => {
            return (
              <RobotoMarkdownLink
                href={href}
                datasetLinkRenderer={datasetLinkRenderer}
                fileLinkRenderer={fileLinkRenderer}
                topicLinkRenderer={topicLinkRenderer}
                messagePathLinkRenderer={messagePathLinkRenderer}
                onLinkClick={onLinkClick}
              >
                {children}
              </RobotoMarkdownLink>
            );
          },
        }}
      >
        {formattedContent}
      </ReactMarkdown>
    </Box>
  );
};
